import { isLiveOneTime } from "@outschool/business-rules";
import {
  Activity,
  EducatorSelectedFormat,
} from "@outschool/gql-backend-generated";
import {
  TFunction,
  useLocalPriceDisplay,
  useTranslation,
} from "@outschool/localization";

import { COACHING_SUBJECTS, LESSON_SUBJECTS } from "../utils/Activity";

function classDurationString(
  activity: Pick<Activity, "isFlexSchedule" | "isOneTime" | "duration_minutes">,
  t: TFunction
): string {
  if (activity.isFlexSchedule) {
    return t("No live meetings"); //todo delete with 1-1 exp release
  } else if (activity.isOneTime) {
    return t("{{numMinutes}} minute class", {
      numMinutes: activity.duration_minutes,
    });
  } else {
    return t("{{numMinutes}} minutes per class", {
      numMinutes: activity.duration_minutes,
    });
  }
}

function classDurationShortString(
  activity: Pick<Activity, "duration_minutes">,
  t: TFunction,
  useHours: boolean
): string {
  if (activity.duration_minutes) {
    if (activity?.duration_minutes >= 60 && useHours) {
      const hours = activity.duration_minutes / 60;
      const hoursDisplay = hours.toFixed(hours % 1 === 0 ? 0 : 2);
      return t("{{numHours}} hr", {
        numHours: hoursDisplay,
      });
    }
    return t("{{numMinutes}} min", {
      numMinutes: activity.duration_minutes,
    });
  } else {
    return "";
  }
}

function classTypeString(
  activity: Pick<
    Activity,
    "isFlexSchedule" | "isSelfPaced" | "isClub" | "size_max" | "subject"
  >,
  sentenceCased: boolean = false,
  t: TFunction
): string {
  if (activity.isClub) {
    return sentenceCased ? t("Chat group") : t("Chat Group");
  } else if (activity.isSelfPaced) {
    return sentenceCased ? t("Self-Paced class") : t("Self-Paced Class");
  } else if (activity.isFlexSchedule) {
    return sentenceCased ? t("Flexible schedule") : t("Flex Class");
  } else if (activity.isSelfPaced) {
    return sentenceCased ? t("Self-paced class") : t("Self-Paced Class");
  } else if (activity.size_max === 1) {
    if (!activity.subject) {
      return sentenceCased ? t("1-on-1 tutoring") : t("1-on-1 Tutoring");
    } else if (COACHING_SUBJECTS.includes(activity.subject)) {
      return sentenceCased ? t("1-on-1 coaching") : t("1-on-1 Coaching");
    } else if (LESSON_SUBJECTS.includes(activity.subject)) {
      return sentenceCased ? t("1-on-1 lessons") : t("1-on-1 Lessons");
    } else {
      return sentenceCased ? t("1-on-1 tutoring") : t("1-on-1 Tutoring");
    }
  } else {
    return sentenceCased ? t("Group class") : t("Group Class");
  }
}

function classFormatString(
  activity: Pick<
    Activity,
    | "classFormat"
    | "isSelfPaced"
    | "hasTeacherSchedule"
    | "isOneTime"
    | "size_max"
  >,
  t: TFunction
) {
  if (activity.isSelfPaced) {
    return activity.classFormat === EducatorSelectedFormat.Course
      ? t("Self-Paced Course")
      : t("Self-Paced Class");
  }
  if (activity.hasTeacherSchedule) {
    return t("Live 1-on-1 Lessons");
  }
  if (activity.isOneTime) {
    return t("Live One-Time Class");
  }
  if (activity.size_max == 1) {
    return activity.classFormat === EducatorSelectedFormat.Course
      ? t("Live Private Course")
      : t("Live Private Class");
  }

  return activity.classFormat === EducatorSelectedFormat.Course
    ? t("Live Group Course")
    : t("Live Group Class");
}

function detailedScheduleString(
  activity: Pick<
    Activity,
    | "weekly_meetings"
    | "duration_weeks"
    | "is_ongoing_weekly"
    | "isClub"
    | "isFlexSchedule"
    | "size_max"
    | "hasTeacherSchedule"
    | "isSelfPaced"
    | "duration_minutes"
  >,
  t: TFunction
): string {
  if (activity.isClub) {
    return t("Unlimited Access");
  } else if (activity.isFlexSchedule) {
    return t("{{numWeeks}} weeks", {
      numWeeks: activity.duration_weeks,
    });
  } else if (activity.isSelfPaced) {
    return t("{{numWeeks}} lessons", {
      numWeeks: activity.duration_weeks,
      //todo change to lessons length
    });
  } else if (activity.size_max === 1) {
    return t("On Demand");
  } else if (activity.is_ongoing_weekly) {
    return (
      t("{{weeklyMeetings}}/wk, ", {
        weeklyMeetings: activity.weekly_meetings ?? 0,
      }) + classDurationShortString(activity, t, true)
    );
  } else {
    return (
      t("{{durationWeeks}} wk, ", {
        defaultValue_plural: "{{durationWeeks}} wks, ",
        count: activity.duration_weeks ?? 0,
        durationWeeks: activity.duration_weeks ?? 0,
      }) +
      t("{{weeklyMeetings}}/wk, ", {
        weeklyMeetings: activity.weekly_meetings ?? 0,
      }) +
      classDurationShortString(activity, t, true)
    );
  }
}

function durationWeeksString(
  activity: Pick<Activity, "is_ongoing_weekly" | "isClub" | "duration_weeks">,
  t: TFunction
): string {
  if (!!activity.is_ongoing_weekly) {
    return t("Runs week after week");
  } else if (activity.isClub) {
    return t("Renews on the 1st of the month");
  } else {
    return t("Over {{durationWeeks}} week", {
      defaultValue_plural: "Over {{durationWeeks}} weeks",
      count: activity.duration_weeks ?? 0,
      durationWeeks: activity.duration_weeks ?? 0,
    });
  }
}

function paymentCancelString(t: TFunction): string {
  return t("Cancel anytime");
}

function paymentFrequencyString(
  activity: Pick<
    Activity,
    | "is_ongoing_weekly"
    | "isClub"
    | "hasTeacherSchedule"
    | "allows_recurring_payment"
  >,
  t: TFunction
): string {
  if (activity.isClub) {
    return t("Charged monthly");
  } else if (
    !!activity.is_ongoing_weekly ||
    activity.allows_recurring_payment
  ) {
    return t("Charged weekly");
  } else if (activity?.hasTeacherSchedule) {
    return t("Charged weekly or per session");
  } else {
    return t("Charged upfront");
  }
}

function paymentFrequencyShortString(
  activity: Pick<
    Activity,
    | "isOneTime"
    | "is_ongoing_weekly"
    | "isClub"
    | "hasTeacherSchedule"
    | "allows_recurring_payment"
    | "weekly_meetings"
    | "isFlexSchedule"
    | "duration_weeks"
    | "price_cents"
  >,
  t: TFunction,
  makePerStudentPriceText: ReturnType<
    typeof useLocalPriceDisplay
  >["makePerStudentPriceText"]
): string {
  if (activity.hasTeacherSchedule) {
    return t("per session");
  } else if (activity.isOneTime) {
    return t("per class");
  } else if (activity.isClub) {
    return t("monthly");
  } else if (
    !!activity.is_ongoing_weekly &&
    activity.weekly_meetings &&
    activity.weekly_meetings >= 1
  ) {
    return t("weekly");
  } else if (activity.isFlexSchedule && !activity.allows_recurring_payment) {
    return t("for {{duration}} weeks", { duration: activity.duration_weeks });
  } else if (activity.isFlexSchedule && activity.allows_recurring_payment) {
    return t("weekly or {{totalPrice}} for {{duration}} weeks", {
      totalPrice: totalPriceString(activity, makePerStudentPriceText),
      duration: activity.duration_weeks,
    });
  } else if (!activity.allows_recurring_payment) {
    return t("for {{classes}} classes", {
      classes: (activity.duration_weeks || 0) * (activity.weekly_meetings || 0),
    });
  } else if (activity.allows_recurring_payment) {
    return t("weekly or {{totalPrice}} for {{classes}} classes", {
      totalPrice: totalPriceString(activity, makePerStudentPriceText),
      classes: (activity.duration_weeks || 0) * (activity.weekly_meetings || 0),
    });
  } else {
    return "";
  }
}

function paymentFrequencySearchString(
  activity: Pick<
    Activity,
    | "is_ongoing_weekly"
    | "isClub"
    | "hasTeacherSchedule"
    | "allows_recurring_payment"
  >,
  t: TFunction
): string {
  if (activity.isClub) {
    return t("Pay monthly");
  } else if (activity?.hasTeacherSchedule) {
    return t("Pay weekly or per session");
  } else if (!!activity.is_ongoing_weekly) {
    return t("Pay weekly");
  } else if (activity.allows_recurring_payment) {
    return t("Pay weekly or upfront");
  } else {
    return t("Pay upfront");
  }
}

function perClassString(
  activity: Pick<Activity, "isFlexSchedule" | "size_max" | "isClub">,
  t: TFunction
): string {
  if (activity.isClub) {
    return t(" per month");
  } else if (activity.isFlexSchedule) {
    return t(" per week");
  } else if (activity.size_max === 1) {
    return t(" per session");
  } else {
    return t(" per class");
  }
}

function perMeetingPriceString(
  activity: Pick<
    Activity,
    | "weekly_meetings"
    | "duration_weeks"
    | "isClub"
    | "price_cents"
    | "is_ongoing_weekly"
    | "hasTeacherSchedule"
  >,
  makePerMeetingPriceText: ReturnType<
    typeof useLocalPriceDisplay
  >["makePerMeetingPriceText"]
): string {
  return makePerMeetingPriceText(activity);
}

function perMeetingPriceCreditsString(
  activity: Pick<
    Activity,
    | "weekly_meetings"
    | "duration_weeks"
    | "isClub"
    | "price_cents"
    | "is_ongoing_weekly"
    | "hasTeacherSchedule"
  >,
  makePerMeetingPriceCreditsText: ReturnType<
    typeof useLocalPriceDisplay
  >["makePerMeetingPriceCreditsText"]
): string {
  return makePerMeetingPriceCreditsText(activity);
}

function totalString(
  activity: Pick<Activity, "is_ongoing_weekly" | "allows_recurring_payment">,
  t: TFunction
) {
  if (!!activity.is_ongoing_weekly || activity.allows_recurring_payment) {
    return t(" per week");
  } else {
    return t(" total");
  }
}

function totalPriceString(
  activity: Pick<
    Activity,
    | "weekly_meetings"
    | "duration_weeks"
    | "isClub"
    | "price_cents"
    | "is_ongoing_weekly"
    | "hasTeacherSchedule"
  >,
  makePerStudentPriceText: ReturnType<
    typeof useLocalPriceDisplay
  >["makePerStudentPriceText"]
): string {
  return makePerStudentPriceText(activity);
}

function videoMeetingsString(
  activity: Pick<Activity, "isFlexSchedule" | "isClub">,
  t: TFunction
) {
  if (activity.isFlexSchedule || activity.isClub) {
    return t("No live video meetings");
  } else {
    return t("Live video meetings");
  }
}

function weeklyMeetingsString(
  activity: Pick<
    Activity,
    "weekly_meetings" | "isClub" | "isFlexSchedule" | "isOneTime"
  >,
  t: TFunction
): string {
  if (activity.isClub) {
    return t("Unlimited access to chat group");
  } else if (activity.isFlexSchedule) {
    return t("Flexible schedule");
  } else if (activity.isOneTime) {
    return t("Meets once");
  } else {
    return t("Meets {{weeklyMeetings}}x per week", {
      weeklyMeetings: activity.weekly_meetings,
    });
  }
}

function weeklyMeetingsSearchString(
  activity: Pick<
    Activity,
    | "weekly_meetings"
    | "duration_weeks"
    | "is_ongoing_weekly"
    | "isClub"
    | "isFlexSchedule"
    | "size_max"
    | "hasTeacherSchedule"
  >,
  t: TFunction,
  shortenWeekText = false
): string {
  if (activity.isClub) {
    return t("Unlimited access");
  } else if (activity.isFlexSchedule) {
    return shortenWeekText
      ? t("{{durationWeeks}} wk", {
          defaultValue_plural: "{{durationWeeks}} wks",
          count: activity.duration_weeks ?? 0,
          durationWeeks: activity.duration_weeks ?? 0,
        })
      : t("{{durationWeeks}} week", {
          defaultValue_plural: "{{durationWeeks}} weeks",
          count: activity.duration_weeks ?? 0,
          durationWeeks: activity.duration_weeks ?? 0,
        });
  } else if (isLiveOneTime(activity)) {
    return t("Meets once");
  } else if (activity.size_max === 1 && activity?.hasTeacherSchedule) {
    return t("Meets on demand");
  } else if (activity.is_ongoing_weekly) {
    return shortenWeekText
      ? t("{{weeklyMeetings}}x /wk", {
          weeklyMeetings: activity.weekly_meetings,
        })
      : t("{{weeklyMeetings}}x per week", {
          weeklyMeetings: activity.weekly_meetings,
        });
  } else {
    return shortenWeekText
      ? t("{{weeklyMeetings}}x /wk, {{durationWeeks}} wk", {
          defaultValue_plural: "{{weeklyMeetings}}x /wk, {{durationWeeks}} wks",
          count: activity.duration_weeks ?? 0,
          durationWeeks: activity.duration_weeks ?? 0,
          weeklyMeetings: activity.weekly_meetings,
        })
      : t("{{weeklyMeetings}}x per week, {{durationWeeks}} week", {
          defaultValue_plural:
            "{{weeklyMeetings}}x per week, {{durationWeeks}} weeks",
          count: activity.duration_weeks ?? 0,
          durationWeeks: activity.duration_weeks ?? 0,
          weeklyMeetings: activity.weekly_meetings,
        });
  }
}

function weeklyPriceString(
  activity: Pick<
    Activity,
    | "weekly_meetings"
    | "duration_weeks"
    | "isClub"
    | "price_cents"
    | "is_ongoing_weekly"
    | "hasTeacherSchedule"
  >,
  makePerWeekPriceText: ReturnType<
    typeof useLocalPriceDisplay
  >["makePerWeekPriceText"]
): string {
  return makePerWeekPriceText(activity);
}

function flexClassroomString(t: TFunction): string {
  return t("Async classroom");
}

function suggestedWeeklyMeetingsString(
  activity: Pick<Activity, "suggestedWeeklyMeetings">,
  t: TFunction
): string {
  if (!activity.suggestedWeeklyMeetings) {
    return "";
  }
  return t("Meets {{suggestedWeeklyMeetings}}x per week (teacher suggestion)", {
    suggestedWeeklyMeetings: activity.suggestedWeeklyMeetings,
  });
}

export type SelfPacedNumberOfLessonsActivityType = {
  syllabus?: {
    lessons?: { uid: string }[] | null;
  } | null;
};

function selfPacedNumberOfLessonsString(
  activity: SelfPacedNumberOfLessonsActivityType,
  t: TFunction
) {
  const lessons = activity.syllabus?.lessons;
  return t("{{numberOfLessons}} pre-recorded lesson", {
    numberOfLessons: lessons?.length ?? 1,
    count: lessons?.length ?? 1,
    defaultValue_plural: "{{numberOfLessons}} pre-recorded lessons",
  });
}

function selfPacedWeeksOfFeedbackString(
  activity: Pick<Activity, "duration_weeks">,
  t: TFunction
) {
  return t("{{durationWeeks}} week of teacher support", {
    defaultValue_plural: "{{durationWeeks}} weeks of teacher support",
    count: activity.duration_weeks ?? 1,
    durationWeeks: activity.duration_weeks ?? 1,
  });
}

function selfPacedAccessToContentString(t: TFunction) {
  return t("1 year of access to the content");
}

function selfPacedStartAndLearnString(t: TFunction) {
  return t("Learn at your own pace");
}

function selfPacedChooseStartTimeString(t: TFunction) {
  return t("Choose your start date");
}

export default function usePriceDetailsString() {
  const { t } = useTranslation("ui-components-website\\hooks\\PriceDetails");
  const {
    makePerMeetingPriceText,
    makePerWeekPriceText,
    makePerStudentPriceText,
    makePerMeetingPriceCreditsText,
  } = useLocalPriceDisplay();
  return {
    classDurationString(
      activity: Pick<
        Activity,
        "isFlexSchedule" | "isOneTime" | "duration_minutes"
      >
    ): string {
      return classDurationString(activity, t);
    },
    classDurationShortString(
      activity: Pick<Activity, "duration_minutes">
    ): string {
      return classDurationShortString(activity, t, false);
    },
    classTypeString(
      activity: Pick<
        Activity,
        "isFlexSchedule" | "isSelfPaced" | "isClub" | "size_max" | "subject"
      >
    ): string {
      return classTypeString(activity, false, t);
    },
    classFormatString(
      activity: Pick<
        Activity,
        | "classFormat"
        | "isSelfPaced"
        | "hasTeacherSchedule"
        | "isOneTime"
        | "size_max"
      >
    ) {
      return classFormatString(activity, t);
    },
    detailedScheduleString(
      activity: Pick<
        Activity,
        | "weekly_meetings"
        | "duration_weeks"
        | "is_ongoing_weekly"
        | "isClub"
        | "isFlexSchedule"
        | "size_max"
        | "hasTeacherSchedule"
        | "isSelfPaced"
        | "duration_minutes"
      >
    ): string {
      return detailedScheduleString(activity, t);
    },
    durationWeeksString(
      activity: Pick<
        Activity,
        "is_ongoing_weekly" | "isClub" | "duration_weeks"
      >
    ): string {
      return durationWeeksString(activity, t);
    },
    flexClassroomString(): string {
      return flexClassroomString(t);
    },
    paymentCancelString(): string {
      return paymentCancelString(t);
    },
    paymentFrequencyString(
      activity: Pick<
        Activity,
        | "is_ongoing_weekly"
        | "isClub"
        | "hasTeacherSchedule"
        | "allows_recurring_payment"
      >
    ): string {
      return paymentFrequencyString(activity, t);
    },
    paymentFrequencyShortString(
      activity: Pick<
        Activity,
        | "isOneTime"
        | "is_ongoing_weekly"
        | "isClub"
        | "hasTeacherSchedule"
        | "allows_recurring_payment"
        | "weekly_meetings"
        | "isFlexSchedule"
        | "duration_weeks"
        | "price_cents"
      >
    ): string {
      return paymentFrequencyShortString(activity, t, makePerStudentPriceText);
    },
    paymentFrequencySearchString(
      activity: Pick<
        Activity,
        | "is_ongoing_weekly"
        | "isClub"
        | "hasTeacherSchedule"
        | "allows_recurring_payment"
      >
    ): string {
      return paymentFrequencySearchString(activity, t);
    },
    perClassString(
      activity: Pick<Activity, "isFlexSchedule" | "size_max" | "isClub">
    ): string {
      return perClassString(activity, t);
    },
    perMeetingPriceString(
      activity: Pick<
        Activity,
        | "weekly_meetings"
        | "duration_weeks"
        | "isClub"
        | "price_cents"
        | "is_ongoing_weekly"
        | "hasTeacherSchedule"
      >
    ): string {
      return perMeetingPriceString(activity, makePerMeetingPriceText);
    },
    perMeetingPriceCreditsString(
      activity: Pick<
        Activity,
        | "weekly_meetings"
        | "duration_weeks"
        | "isClub"
        | "price_cents"
        | "is_ongoing_weekly"
        | "hasTeacherSchedule"
      >
    ): string {
      return perMeetingPriceCreditsString(
        activity,
        makePerMeetingPriceCreditsText
      );
    },
    suggestedWeeklyMeetingsString(
      activity: Pick<Activity, "suggestedWeeklyMeetings">
    ): string {
      return suggestedWeeklyMeetingsString(activity, t);
    },
    totalString(
      activity: Pick<Activity, "is_ongoing_weekly" | "allows_recurring_payment">
    ): string {
      return totalString(activity, t);
    },
    totalPriceString(
      activity: Pick<
        Activity,
        | "weekly_meetings"
        | "duration_weeks"
        | "isClub"
        | "price_cents"
        | "is_ongoing_weekly"
        | "hasTeacherSchedule"
      >
    ): string {
      return totalPriceString(activity, makePerStudentPriceText);
    },
    videoMeetingsString(
      activity: Pick<Activity, "isFlexSchedule" | "isClub">
    ): string {
      return videoMeetingsString(activity, t);
    },
    weeklyMeetingsString(
      activity: Pick<
        Activity,
        | "weekly_meetings"
        | "duration_weeks"
        | "is_ongoing_weekly"
        | "duration_minutes"
        | "isClub"
        | "isFlexSchedule"
        | "isOneTime"
      >
    ): string {
      return weeklyMeetingsString(activity, t);
    },
    weeklyMeetingsSearchString(
      activity: Pick<
        Activity,
        | "weekly_meetings"
        | "duration_weeks"
        | "is_ongoing_weekly"
        | "duration_minutes"
        | "isClub"
        | "isFlexSchedule"
        | "size_max"
        | "hasTeacherSchedule"
      >,
      shortenWeekText = false
    ): string {
      return weeklyMeetingsSearchString(activity, t, shortenWeekText);
    },
    weeklyPriceString(
      activity: Pick<
        Activity,
        | "weekly_meetings"
        | "duration_weeks"
        | "isClub"
        | "price_cents"
        | "is_ongoing_weekly"
        | "hasTeacherSchedule"
      >
    ): string {
      return weeklyPriceString(activity, makePerWeekPriceText);
    },
    selfPacedNumberOfLessonsString(
      activity: SelfPacedNumberOfLessonsActivityType
    ): string {
      return selfPacedNumberOfLessonsString(activity, t);
    },
    selfPacedWeeksOfFeedbackString(
      activity: Pick<Activity, "duration_weeks">
    ): string {
      return selfPacedWeeksOfFeedbackString(activity, t);
    },
    selfPacedAccessToContentString() {
      return selfPacedAccessToContentString(t);
    },
    selfPacedStartAndLearnString() {
      return selfPacedStartAndLearnString(t);
    },
    selfPacedChooseStartTimeString() {
      return selfPacedChooseStartTimeString(t);
    },
  };
}
